import React from 'react';
import * as mui from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded';
import CopyAllRoundedIcon from '@mui/icons-material/CopyAllRounded';
import NormalTextField from '../../../common/NormalTextField';
import ModernSelect from '../../../common/ModernSelect';

// Modern minimalist styled components
const StyledCard = styled(mui.Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: 'none',
  border: '1px solid',
  borderColor: alpha(theme.palette.divider, 0.5),
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}));

const SectionTitle = styled(mui.Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const UserAccessReviewTab = (props) => {
  const {
    selectedApps,
    uarData,
    frequency,
    scopeusers,
    scoperoles,
    extractor,
    istool,
    SOD,
    extractsend,
    reviewer,
    cnareviewer,
    changedoc,
    changedocsend,
    changedocreviewed,
    frequencyOptions,
    scopeusersOptions,
    scoperolesOptions,
    extractorOptions,
    toolOptions,
    documentationOptions,
    reviewOptions,
    cnareviewOptions,
    handleOpenCopyModal,
    handleFrequencyChange,
    handleUARChange,
    handleUARBlur,
    handleScopeUsersChange,
    handleScopeRolesChange,
    handleExtractorChange,
    handleIsToolChange,
    handleExtractSendChange,
    handleReviewerChange,
    handleCNAReviewerChange,
    handleChangeDocChange,
    handleChangeDocSendChange,
    handleChangeDocResolutionChange,
    handleSODChange,
  } = props;

  return (
    <div>
      <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', textAlign: 'center' }}>
        <mui.Tooltip title="Copy Process" onClick={handleOpenCopyModal}>
          <mui.IconButton>
            <CopyAllRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>

        <mui.Tooltip title="Certify Accuracy">
          <mui.IconButton>
            <HistoryEduRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>

        <mui.Tooltip title="Download Process Narrative">
          <mui.IconButton>
            <FileDownloadRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>
      </mui.Box>

      <StyledCard>
        <SectionTitle variant="overline">Frequency and Timeline</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          What is the frequency of the user access review for {selectedApps.APP_NAME}?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Review Frequency"
            isMultiSelect={false}
            placeholderText="Select Frequency"
            selectedOptions={frequency}
            selectOptions={frequencyOptions}
            handleChange={handleFrequencyChange}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          How many days must the access review be completed upon initiation?
        </mui.Typography>
        <NormalTextField
          name="DAYS_TO_COMPLETE"
          value={uarData.DAYS_TO_COMPLETE}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
          type="number"
          min="0" 
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Scope of Review</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Which accounts are reviewed as part of the access review process?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Account Types"
            isMultiSelect={true}
            placeholderText="Select Accounts"
            selectedOptions={scopeusers}
            selectOptions={scopeusersOptions}
            handleChange={handleScopeUsersChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          Which accounts/roles are exempted from the user access review?
        </mui.Typography>
        <NormalTextField
          name="SCOPE_EXEMPTED"
          value={uarData.SCOPE_EXEMPTED}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
        />

        <mui.Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
          What is the coverage of the review?
        </mui.Typography>
        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Review Coverage"
            isMultiSelect={true}
            placeholderText="Select Coverage"
            selectedOptions={scoperoles}
            selectOptions={scoperolesOptions}
            handleChange={handleScopeRolesChange}
          />
        </div>
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">User Data Extraction</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Which team is responsible in extracting user list within {selectedApps.APP_NAME}?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Data Extraction Team"
            isMultiSelect={true}
            placeholderText="Select Extractor"
            selectedOptions={extractor}
            selectOptions={extractorOptions}
            handleChange={handleExtractorChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Is there a tool used to extract users access report?
        </mui.Typography>
        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Tool Usage"
            isMultiSelect={false}
            placeholderText="Select Confirmation"
            selectedOptions={istool}
            selectOptions={toolOptions}
            handleChange={handleIsToolChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If a tool is used to extract, type the name of the tool below
        </mui.Typography>
        <NormalTextField
          name="TOOL_NAME"
          value={uarData.TOOL_NAME}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
        />

        <mui.Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
          How is the extracted user access report sent to the review owners?
        </mui.Typography>
        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Submission Method"
            isMultiSelect={true}
            placeholderText="Select Submission Channel"
            selectedOptions={extractsend}
            selectOptions={documentationOptions}
            handleChange={handleExtractSendChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          Provide details if the method of submission is not included in the items above
        </mui.Typography>
        <NormalTextField
          name="EXTRACTION_SEND_OTHER"
          value={uarData.EXTRACTION_SEND_OTHER}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Reviewer</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Which team is responsible in reviewing and approving the user access report?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Review Team"
            isMultiSelect={true}
            placeholderText="Select Reviewer"
            selectedOptions={reviewer}
            selectOptions={reviewOptions}
            handleChange={handleReviewerChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If reviewer/team is not included in the list above, type the reviewer/team name of the reviewer
        </mui.Typography>
        <NormalTextField
          name="REVIEWER_OTHER"
          value={uarData.REVIEWER_OTHER}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Completeness and Accuracy Check</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Which team is responsible in performing the completeness and accuracy check of the access reports?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Completeness Check Team"
            isMultiSelect={true}
            placeholderText="Select Team"
            selectedOptions={cnareviewer}
            selectOptions={cnareviewOptions}
            handleChange={handleCNAReviewerChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          How is the completeness and accuracy check performed and documented?
        </mui.Typography>
        <NormalTextField
          name="CNA_PROCESS"
          value={uarData.CNA_PROCESS}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
          isMultiLine={true}
          rows="5"
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Requested Access Changes</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Where is the review owner's decision on access request changes recorded?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Documentation Method"
            isMultiSelect={true}
            placeholderText="Select Documentation Method"
            selectedOptions={changedoc}
            selectOptions={documentationOptions}
            handleChange={handleChangeDocChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If documentation method is not included in the list above, provide details below
        </mui.Typography>
        <NormalTextField
          name="CHANGE_DOC_OTHER"
          value={uarData.CHANGE_DOC_OTHER}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
        />

        <mui.Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
          How are the change requests sent to responsible parties for implementation?
        </mui.Typography>
        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Request Submission Method"
            isMultiSelect={true}
            placeholderText="Select Submission Method"
            selectedOptions={changedocsend}
            selectOptions={documentationOptions}
            handleChange={handleChangeDocSendChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If submission method is not included in the list above, provide details below
        </mui.Typography>
        <NormalTextField
          name="CHANGE_DOCUMENTATION_SEND_OTHER"
          value={uarData.CHANGE_DOCUMENTATION_SEND_OTHER}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
        />

        <mui.Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
          What is the timeline to complete the access changes requested by the review owner (in days)?
        </mui.Typography>
        <NormalTextField
          name="CHANGE_TIMELINE"
          value={uarData.CHANGE_TIMELINE}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
          type="number"
          min="1"
          inputProps={{ min: 1 }}
          helperText="Minimum 1 day required"
        />

        <mui.Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
          How is the resolution of the requested changes documented?
        </mui.Typography>
        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Resolution Documentation"
            isMultiSelect={true}
            placeholderText="Select Documentation Method"
            selectedOptions={changedocreviewed}
            selectOptions={documentationOptions}
            handleChange={handleChangeDocResolutionChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If documentation method is not included in the list above, provide details below
        </mui.Typography>
        <NormalTextField
          name="CHANGE_DOC_RESOLUTION_OTHER"
          value={uarData.CHANGE_DOC_RESOLUTION_OTHER}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Segregation of Duties</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Does the access review include a review of segregation of duties?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="SOD Review"
            isMultiSelect={false}
            placeholderText="Select Confirmation"
            selectedOptions={SOD}
            selectOptions={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ]}
            handleChange={handleSODChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If yes, provide details below on how the segregation of duties is reviewed
        </mui.Typography>
        <NormalTextField
          name="SOD_PROCESS"
          value={uarData.SOD_PROCESS}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
          isMultiLine={true}
          rows="5"
        />

        <mui.Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
          Describe how segregation of duties conflicts are mitigated:
        </mui.Typography>
        <NormalTextField
          name="SOD_MITIGATION"
          value={uarData.SOD_MITIGATION}
          onChange={handleUARChange}
          onBlur={handleUARBlur}
          isMultiLine={true}
          rows="5"
        />
      </StyledCard>
    </div>
  );
};

export default UserAccessReviewTab; 